import config from 'constants/config'
import { createSelector } from 'reselect'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { NAVBAR_SPECIFIC_ORDER_REGIONS_LIST } from 'constants/config/region'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import { getNavItemsFlat } from 'constants/config/navItemsByRegion'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

export const getNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => isLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => config.marketplace.isEnabled && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.marketplacePage],
  (account, isLuxPlusEnabled, isLoyaltyEnabled, geo, isStoreMode, isMarketplacePageEnabled) => {
    const navEnabled = (nav: App.NavItem) => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(geo.currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(geo.currentRegionCode)) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.marketplaceToggle && !isMarketplacePageEnabled) {
        return false
      }

      return true
    }

    const navRegionConfig = NAVBAR_SPECIFIC_ORDER_REGIONS_LIST.has(geo.currentRegionCode) ? geo.currentRegionCode : 'DEFAULT'
    const navItems = Array.isArray(config.header.nav.items) ? config.header.nav.items : config.header.nav.items[navRegionConfig]
    return navItems.reduce<Array<App.NavItem>>((navList, nav) => {
      if (nav.newLabelOnlyOnRegions) nav.newLabel = nav.newLabelOnlyOnRegions.includes(geo.currentRegionCode)

      if (nav.hasDropdown) {
        // need to whittle down the dropdown items of this subnav
        // note: subnavs can only go 1 level deep at most

        const subNav = {
          ...nav,
          items: nav.items?.filter(navEnabled),
        }
        if (subNav.items && subNav.items.length > 0) {
          navList.push(subNav)
        }
      } else if (navEnabled(nav)) {
        // normal nav, see if we can push it in
        navList.push(nav)
      }
      return navList
    }, [])
  },
)

/**
 * Retrieves the nav items flattened
 * This is used in the luxkit navbar
 */
export const getFlatNavItems = createSelector(
  (state: App.State) => state.auth.account,
  (state: App.State) => isLuxPlusEnabled(state),
  (state: App.State) => getIsLoyaltyEnabled(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.system.storeMode,
  (state: App.State) => config.marketplace.isEnabled && state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.marketplacePage],
  (account, isLuxPlusEnabled, isLoyaltyEnabled, currentRegionCode, isStoreMode, isMarketplacePageEnabled): Array<App.NavItem> => {
    return getNavItemsFlat().filter(nav => {
      if (nav.onlyOnRegions && !nav.onlyOnRegions.includes(currentRegionCode)) {
        return false
      }

      if (nav.notOnRegions?.includes(currentRegionCode)) {
        return false
      }

      if (nav.featureToggle && !config[nav.featureToggle]) {
        return false
      }

      if (nav.accountToggle && (!account[nav.accountToggle])) {
        return false
      }

      if (!!nav.luxPlusToggle && !isLuxPlusEnabled) {
        return false
      }

      if (!!nav.loyaltyToggle && !isLoyaltyEnabled) {
        return false
      }

      if (nav.hideInStoreMode && isStoreMode) {
        return false
      }

      if (nav.marketplaceToggle && !isMarketplacePageEnabled) {
        return false
      }

      return true
    },
    )
  },
)
